import React from 'react';
import { FadeIn } from '../components/FadeIn';
import { SectionLabel } from '../components/SectionLabel';
import { CtaButton } from '../components/CtaButton';
import { buildCheckoutUrl, handleCheckoutClick } from '../utils/tracking';
import { BookIcon, MessageCircleIcon, WindIcon, LockIcon, MailIcon, ShieldCheckIcon, MonitorIcon } from 'lucide-react';
export function PriceSection() {
  return <section className="py-14 md:py-20 px-6 bg-[#112031]" data-id="element-311">
      <div className="max-w-4xl mx-auto text-center" data-id="element-312">
        <FadeIn data-id="element-313">
          <SectionLabel text="Let's Put This In Perspective" data-id="element-314" />
          <h2 className="font-playfair text-3xl md:text-5xl font-bold text-warmwhite mb-16 leading-tight" data-id="element-315">
            Less Than Your Morning Coffee.
            <br data-id="element-316" />
            <span className="text-rosegold" data-id="element-317">
              More Powerful Than 10 Years of Counting to Ten.
            </span>
          </h2>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24" data-id="element-318">
          {/* Coffee Card */}
          <FadeIn delay={0.2} direction="right" data-id="element-319">
            <div className="bg-midnight/50 border border-white/5 rounded-2xl p-8 h-full flex flex-col items-center text-center opacity-70" data-id="element-320">
              <span className="text-6xl mb-6" data-id="element-321">☕</span>
              <h3 className="font-inter font-semibold text-softgray text-xl mb-2" data-id="element-322">
                Your Morning Latte
              </h3>
              <div className="font-playfair text-4xl font-bold text-warmwhite mb-4" data-id="element-323">
                $6.50
              </div>
              <p className="font-inter text-softgray" data-id="element-324">
                Gone in 4 minutes.
                <br data-id="element-325" />
                Problem still there at 4PM.
              </p>
            </div>
          </FadeIn>

          {/* Product Card */}
          <FadeIn delay={0.4} direction="left" data-id="element-326">
            <div className="bg-midnight border-2 border-rosegold/50 rounded-2xl p-8 h-full flex flex-col items-center text-center shadow-[0_0_30px_rgba(201,169,110,0.15)] relative overflow-hidden" data-id="element-327">
              <div className="absolute top-0 right-0 w-32 h-32 bg-rosegold/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" data-id="element-328" />
              <span className="text-6xl mb-6 text-rosegold drop-shadow-[0_0_10px_rgba(201,169,110,0.5)]" data-id="element-329">
                ⚡
              </span>
              <h3 className="font-inter font-bold text-warmwhite text-xl mb-2" data-id="element-330">
                The Anger Circuit Breaker
              </h3>
              <div className="font-playfair text-5xl font-bold text-rosegold mb-4" data-id="element-331">
                $6.37
              </div>
              <p className="font-inter text-warmwhite/90 font-medium" data-id="element-332">
                Works in 55 seconds.
                <br data-id="element-333" />
                Yours forever. Used every day.
              </p>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* VALUE STACK SECTION (Integrated) */}
      <div id="value-stack-section" className="max-w-[900px] mx-auto relative z-10 mt-12 scroll-mt-10" data-id="element-334">
        <FadeIn className="text-center mb-12" data-id="element-335">
          <SectionLabel text="VALUE STACK" data-id="element-336" />
          <h2 className="font-playfair text-3xl md:text-5xl font-bold text-warmwhite mb-6 leading-tight" data-id="element-337">
            For Less Than a Coffee… You Get ALL 3
          </h2>
          <p className="font-inter text-lg text-softgray max-w-[650px] mx-auto" data-id="element-338">
            Not 'one tip.' Not 'a maybe.' A complete instant-use system + real
            support + an anxiety shut-off reset.
          </p>
        </FadeIn>

        <div className="space-y-4 md:space-y-6 mb-16" data-id="element-339">
          {/* Card 1: Main Guide */}
          <FadeIn delay={0.1} data-id="element-340">
            <div className="bg-midnight/60 border border-rosegold/20 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-lg hover:-translate-y-1 transition-transform duration-300" data-id="element-341">
              <div className="flex items-start md:items-center gap-5 md:gap-6 flex-1" data-id="element-342">
                <div className="w-12 h-12 rounded-full bg-rosegold/10 flex items-center justify-center shrink-0" data-id="element-343">
                  <BookIcon className="w-6 h-6 text-rosegold" data-id="element-344" />
                </div>
                <div data-id="element-345">
                  <h3 className="font-inter font-bold text-warmwhite text-lg md:text-xl mb-2" data-id="element-346">
                    Main Guide: The 55-Second Anger Reset
                  </h3>
                  <p className="font-inter text-softgray text-sm md:text-base mb-3 leading-relaxed" data-id="element-347">
                    Step-by-step protocol to drop the spike and regain
                    self-control before you snap.
                  </p>
                  <p className="font-inter text-xs text-warmwhite/80 font-medium" data-id="element-348">
                    ✅ Instant access • ✅ Mobile friendly • ✅ Use anywhere
                  </p>
                </div>
              </div>
              <div className="flex md:flex-col items-center md:items-end justify-between w-full md:w-auto mt-2 md:mt-0 border-t border-white/5 md:border-t-0 pt-4 md:pt-0 shrink-0" data-id="element-349">
                <span className="font-inter text-softgray line-through text-lg md:mb-1" data-id="element-350">
                  $44
                </span>
                <span className="font-inter font-bold text-warmwhite text-lg md:text-xl" data-id="element-351">
                  Included
                </span>
              </div>
            </div>
          </FadeIn>

          {/* Card 2: Bonus 1 */}
          <FadeIn delay={0.2} data-id="element-352">
            <div className="bg-midnight/60 border border-rosegold/20 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-lg hover:-translate-y-1 transition-transform duration-300" data-id="element-353">
              <div className="flex items-start md:items-center gap-5 md:gap-6 flex-1" data-id="element-354">
                <div className="w-12 h-12 rounded-full bg-rosegold/10 flex items-center justify-center shrink-0" data-id="element-355">
                  <MessageCircleIcon className="w-6 h-6 text-rosegold" data-id="element-356" />
                </div>
                <div data-id="element-357">
                  <h3 className="font-inter font-bold text-warmwhite text-lg md:text-xl mb-2" data-id="element-358">
                    BONUS #1: Direct Contact With Elena Rostova
                  </h3>
                  <p className="font-inter text-softgray text-sm md:text-base leading-relaxed" data-id="element-359">
                    Ask questions and get guidance so you never feel alone while
                    applying the method.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 w-full md:w-auto mt-2 md:mt-0 border-t border-white/5 md:border-t-0 pt-4 md:pt-0 shrink-0 justify-between md:justify-end" data-id="element-360">
                <span className="font-inter text-softgray line-through text-lg" data-id="element-361">
                  $35
                </span>
                <span className="bg-[#19C37D] text-white font-inter font-bold text-sm px-4 py-1.5 rounded-full uppercase tracking-wider animate-glow-green" data-id="element-362">
                  FREE
                </span>
              </div>
            </div>
          </FadeIn>

          {/* Card 3: Bonus 2 */}
          <FadeIn delay={0.3} data-id="element-363">
            <div className="bg-midnight/60 border border-rosegold/20 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-lg hover:-translate-y-1 transition-transform duration-300" data-id="element-364">
              <div className="flex items-start md:items-center gap-5 md:gap-6 flex-1" data-id="element-365">
                <div className="w-12 h-12 rounded-full bg-rosegold/10 flex items-center justify-center shrink-0" data-id="element-366">
                  <WindIcon className="w-6 h-6 text-rosegold" data-id="element-367" />
                </div>
                <div data-id="element-368">
                  <h3 className="font-inter font-bold text-warmwhite text-lg md:text-xl mb-2" data-id="element-369">
                    BONUS #2: Aftershock Reset (55 seconds for Anxiety)
                  </h3>
                  <p className="font-inter text-softgray text-sm md:text-base leading-relaxed" data-id="element-370">
                    The 'close-the-loop' breathing sequence to shut down
                    post-anger anxiety, racing thoughts, and chest tightness.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 w-full md:w-auto mt-2 md:mt-0 border-t border-white/5 md:border-t-0 pt-4 md:pt-0 shrink-0 justify-between md:justify-end" data-id="element-371">
                <span className="font-inter text-softgray line-through text-lg" data-id="element-372">
                  $35
                </span>
                <span className="bg-[#19C37D] text-white font-inter font-bold text-sm px-4 py-1.5 rounded-full uppercase tracking-wider animate-glow-green" data-id="element-373">
                  FREE
                </span>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* Total Anchor Row */}
        <FadeIn delay={0.4} className="flex flex-col items-center text-center" data-id="element-374">
          {/* Product Bundle Image */}
          <div className="relative w-full max-w-md md:max-w-lg mx-auto mb-12 mt-4" data-id="element-375">
            <div className="absolute -inset-4 bg-rosegold/10 rounded-full blur-3xl animate-glow-shield pointer-events-none" data-id="element-376" />
            <img src="https://cdn.magicpatterns.com/uploads/qbW1fWoWEp2XhXnZimoP2u/a202658d-6133-45ba-89fb-bbdd80204352.png" alt="The Anger Circuit Breaker Digital Bundle" className="relative z-10 w-full h-auto rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/10" data-id="element-377" />

            {/* Digital Edition Badge */}
            <div className="absolute -top-4 -right-2 md:-right-6 z-20 bg-midnight border-2 border-rosegold text-rosegold font-inter font-bold text-xs md:text-sm px-4 py-2 rounded-full shadow-[0_0_15px_rgba(201,169,110,0.4)] flex items-center gap-2 transform rotate-3" data-id="element-378">
              <MonitorIcon className="w-4 h-4" data-id="element-379" />
              DIGITAL EDITION
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 mb-8" data-id="element-380">
            <span className="font-inter text-softgray text-xl line-through decoration-white/30" data-id="element-381">
              Total Value: $114
            </span>
            <div className="relative" data-id="element-382">
              <span className="font-playfair text-4xl md:text-5xl font-bold text-warmwhite" data-id="element-383">
                Today: $6.37
              </span>
              <div className="absolute -bottom-2 left-0 w-full h-1 bg-rosegold/80 rounded-full" data-id="element-384" />
            </div>
          </div>

          <h3 className="font-playfair text-2xl md:text-3xl text-warmwhite italic mb-10 max-w-2xl" data-id="element-385">
            "Are you really going to keep paying with your peace… over $6.37?"
          </h3>

          <CtaButton text="Get Everything Now — $6.37 →" href={buildCheckoutUrl('https://pay.hotmart.com/E105448579Q?bid=1776547527273')} onClick={e => handleCheckoutClick(e, 'https://pay.hotmart.com/E105448579Q?bid=1776547527273')} strongPulse={true} className="w-full md:w-auto md:min-w-[400px] mb-6" data-id="element-386" />

          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3 font-inter text-sm md:text-base" data-id="element-387">
            <span className="flex items-center text-softgray" data-id="element-388">
              <LockIcon className="w-4 h-4 mr-2 text-rosegold" data-id="element-389" /> Secure
              checkout
            </span>
            <span className="text-softgray/40 hidden md:inline" data-id="element-390">•</span>
            <span className="flex items-center text-softgray" data-id="element-391">
              <MailIcon className="w-4 h-4 mr-2 text-rosegold" data-id="element-392" /> Instant email
              delivery
            </span>
            <span className="text-softgray/40 hidden md:inline" data-id="element-393">•</span>
            <span className="flex items-center text-softgray" data-id="element-394">
              <ShieldCheckIcon className="w-4 h-4 mr-2 text-rosegold" data-id="element-395" /> 7-day
              guarantee
            </span>
          </div>
        </FadeIn>
      </div>
    </section>;
}